import React, { useContext } from 'react'
import { css } from '@emotion/core'
import { Card, ResponsiveTableRow } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { DmcResponsiveTable } from '../../common/DmcResponsiveTable'
import { CtrLink } from '../../app/Links'
import { UserContext } from '../UserContext'
import TempleLabel from '../../common/TempleLabel'
import useTempleDirectory from '../../../hooks/useTempleDirectory'

const breakpoints = [576, 768, 992]

const mq = breakpoints.map((bp) => `@media (min-width: ${bp}px)`)

const flexContainer = css`
  display: flex;
  flex-wrap: wrap;
  font-size: 0.75rem;
  margin-bottom: 1em;
  padding: 0.5em 1em;
  border: 1px solid #efefef;
  border-radius: 5px;
`
const flexItem = css`
  width: 100%;
  ${mq[0]} {
    width: 50%;
  }
  ${mq[1]} {
    width: 33%;
  }
  ${mq[2]} {
    width: 33%;
  }
`

const UserOrdinances = () => {
  const userContext = useContext(UserContext)
  const currentUser = userContext.currentUser
  const dmcUserMembership = currentUser.dmcUserMembership
  const dmcOrdinanceList = dmcUserMembership.dmcOrdinanceList
  const isNotAccountable = dmcUserMembership.isNotAccountable
  const isBornInCovenant = dmcUserMembership.isBornInCovenant
  const hasOrdinanceApprovalRequired = dmcUserMembership.hasOrdinanceApprovalRequired
  const anyOrdinanceFlags = isBornInCovenant || hasOrdinanceApprovalRequired || isNotAccountable
  const displayOrdinances = dmcOrdinanceList ? dmcOrdinanceList.length > 0 : false
  const { temples } = useTempleDirectory()
  const [t] = useTranslation()

  const renderOrdinances = () => {
    return (
      <Card>
        <DmcResponsiveTable
          stickyHeader={false}
          atSizeColumns={{
            default: {
              labels: [
                t('ordinances.ordinance', 'Ordinance'),
                t('ctrId', 'CTR ID'),
                t('date', 'Date'),
                t('temple.label', 'Temple'),
                t('source', 'Source'),
                t('living', 'Living'),
              ],
            },
            sm: {
              labels: [
                t('ordinances.headers.ordinanceDateCTR', 'Ordinance / Date / CTR ID'),
                t('ordinances.headers.templeSourceLiving', 'Temple / Source / Living'),
              ],
              combinationRules: [
                ['column1', 'column3', 'column2'],
                ['column4', 'column5', 'column6'],
              ],
            },
            md: {
              labels: [
                t('ordinances.headers.ordinanceAndCTR', 'Ordinance & CTR'),
                t('ordinances.headers.dateAndTemple', 'Date & Temple'),
                t('ordinances.headers.sourceAndLiving', 'Source & Living'),
              ],
              combinationRules: [
                ['column1', 'column2'],
                ['column3', 'column4'],
                ['column5', 'column6'],
              ],
            },
            xl: {
              labels: [
                t('ordinances.ordinance', 'Ordinance'),
                t('ctrId', 'CTR ID'),
                t('date', 'Date'),
                t('temple.label', 'Temple'),
                t('source', 'Source'),
                t('living', 'Living'),
              ],
            },
          }}
          totalColumnCount={6}
          separator={<br />}
        >
          {dmcOrdinanceList.map((ord) => (
            <ResponsiveTableRow
              key={ord.type
                .concat('_')
                .concat(ord.ctrId ? ord.type.substr(1, 3) : ord.ctrId)
                .concat('_')
                .concat(ord.performedDate)}
              column1={t(`ctr.ordinanceType.${ord.type}`, ord.type)}
              column2={
                ord.ctrId ? (
                  <>
                    <CtrLink ctrId={ord.ctrIdEncoded} otherTab /> {`(${ord.ctrId})`}
                  </>
                ) : null
              }
              column3={ord.performedDate}
              column4={temples && <TempleLabel temple={temples[ord.temple]} />}
              column5={t(`ordinance.sourceType.${ord.cmisSource}`, ord.cmisSource)}
              column6={ord.living === true ? t('yes', 'Yes') : null}
            />
          ))}
        </DmcResponsiveTable>
      </Card>
    )
  }

  const renderOrdinanceFlags = () => (
    <div css={flexContainer}>
      {isBornInCovenant && (
        <div data-testid="bornInCovenant" css={flexItem}>
          {t('ordinances.flags.bornInTheCovenant', 'Born in the Covenant')}
        </div>
      )}
      {hasOrdinanceApprovalRequired && (
        <div data-testid="ordinanceApprovalRequired" css={flexItem}>
          {t('accessControl.ordApprovalRequired', 'Ordinance Approval Required')}
        </div>
      )}
      {isNotAccountable && (
        <div data-testid="notAccountable" css={flexItem}>
          {t('ordinances.flags.notAccountable', 'Not Accountable')}
        </div>
      )}
    </div>
  )

  const renderNoOrdinances = () => (
    <div css={flexContainer}>{t('ordinances.noOrdinances', 'No ordinances to display')}</div>
  )

  return (
    <>
      {anyOrdinanceFlags && renderOrdinanceFlags()}
      {!anyOrdinanceFlags && !displayOrdinances && renderNoOrdinances()}
      {displayOrdinances && renderOrdinances()}
    </>
  )
}

export default UserOrdinances
